import { useMemo, useState } from 'react';
import { useTenants, useWebhookEvents } from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { formatNumber, formatTimestamp } from '../api';

const STATUS_FILTERS = ['', 'received', 'processing', 'processed', 'failed', 'duplicate'];

export default function WebhooksPage() {
  const tenants = useTenants();
  const tenantCodes = useMemo(() => tenants.data?.data.map((t) => t.code) ?? [], [tenants.data]);

  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const visibleTenants = tenantFilter ? [tenantFilter] : tenantCodes;

  return (
    <div className="space-y-3">
      <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700 border-b border-neutral-300 pb-1">
        Webhook-Aktivitaet (Cross-Tenant)
      </h1>

      <div className="panel">
        <div className="panel-h">Filter</div>
        <div className="panel-b grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Tenant</label>
            <select
              className="input w-full"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            >
              <option value="">— alle —</option>
              {tenantCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Status</label>
            <select
              className="input w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s || '— alle —'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {tenants.isLoading && <Loading />}
      {tenants.error && <ErrorBox error={tenants.error} />}

      {visibleTenants.map((code) => (
        <TenantWebhookBlock
          key={code}
          tenantCode={code}
          {...(statusFilter ? { status: statusFilter } : {})}
        />
      ))}
    </div>
  );
}

function TenantWebhookBlock({ tenantCode, status }: { tenantCode: string; status?: string }) {
  const filter = { ...(status ? { status } : {}), limit: 200, offset: 0 };
  const events = useWebhookEvents(tenantCode, filter);
  const lastUpdated =
    events.dataUpdatedAt > 0 ? formatTimestamp(new Date(events.dataUpdatedAt).toISOString()) : '-';

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>{tenantCode}</span>
        <div className="ml-auto mr-2 flex items-center gap-2 text-[10px] text-neutral-500">
          <Status kind={events.isFetching ? 'warn' : 'ok'}>
            {events.isFetching ? 'sync' : 'live'}
          </Status>
          <span className="font-mono">{lastUpdated}</span>
        </div>
        <span className="text-[10px] text-neutral-500 font-mono">
          {events.data ? `${events.data.data.length}/${events.data.total}` : '–'}
        </span>
      </div>
      <div className="panel-b p-0">
        {events.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {events.error && (
          <div className="p-2">
            <ErrorBox error={events.error} />
          </div>
        )}
        {events.data && events.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Keine Webhook-Events fuer diesen Filter.</EmptyBox>
          </div>
        )}
        {events.data && events.data.data.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Empfangen</th>
                <th>Source</th>
                <th>Event-Type</th>
                <th>External-ID</th>
                <th>Sig?</th>
                <th>Status</th>
                <th className="num">Tries</th>
                <th>Fehler</th>
              </tr>
            </thead>
            <tbody>
              {events.data.data.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{formatTimestamp(r.received_at)}</td>
                  <td className="mono">{r.source_system}</td>
                  <td className="mono">{r.event_type}</td>
                  <td className="mono text-[11px]">{r.external_event_id ?? ''}</td>
                  <td>
                    {r.signature_valid === true && <Status kind="ok">ok</Status>}
                    {r.signature_valid === false && <Status kind="err">FAIL</Status>}
                    {r.signature_valid === null && <Status kind="muted">–</Status>}
                  </td>
                  <td>
                    <Status kind={statusKindForJobStatus(r.status)}>{r.status}</Status>
                  </td>
                  <td className="num">{formatNumber(r.attempts)}</td>
                  <td className="text-[11px] text-st-err max-w-[300px] truncate">
                    {r.error_message ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
